import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ivLogo from '@/assets/iv-talent-logomark.png';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

type Step = 'email' | 'code';

export default function SignIn() {
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(() => (params.get('email') ?? '').trim().toLowerCase());
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();

  const requestCode = async (): Promise<boolean> => {
    const { data, error } = await supabase.functions.invoke('request-signin-code', {
      body: { email: email.trim().toLowerCase() },
    });
    if (error || (data && (data as any).error)) {
      const msg = (data as any)?.error ?? error?.message ?? 'Could not send code';
      toast({ title: 'Could not send code', description: msg, variant: 'destructive' });
      return false;
    }
    return true;
  };

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const ok = await requestCode();
    setSending(false);
    if (ok) setStep('code');
  };


  const verifyCode = async (token: string) => {
    setVerifying(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token,
      type: 'email',
    });
    console.log('[auth/signin] verifyOtp response:', { data, error });
    setVerifying(false);
    if (error) {
      console.error('[auth/signin] verifyOtp error:', error);
      toast({ title: 'Invalid code', description: error.message, variant: 'destructive' });
      setCode('');
      return;
    }
    // Session is live. /auth/callback completes the post-login default
    // routing (employee bootstrap → welcome/employee landing).
    // Preserve ?next= so flows like /auth/claim?org=… resume after sign-in.
    const next = params.get('next');
    const cbHref = next
      ? `/auth/callback?next=${encodeURIComponent(next)}`
      : '/auth/callback';
    navigate(cbHref, { replace: true });
  };

  const onCodeChange = (v: string) => {
    setCode(v);
    if (v.length === 8 && !verifying) {
      void verifyCode(v);
    }
  };

  const resend = async () => {
    setSending(true);
    const ok = await requestCode();
    setSending(false);
    if (ok) toast({ title: 'New code sent', description: `Check ${email}.` });
  };


  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-background p-4 gap-6">
      <Link to="/" aria-label="Go to home" className="inline-block transition-opacity hover:opacity-80">
        <img src={ivLogo} alt="IV Talent" className="h-16 w-16 rounded-lg" />
      </Link>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-heading">Sign in</CardTitle>
          <CardDescription>
            {step === 'email'
              ? 'We’ll email you an 8-digit code. No password.'
              : `Enter the 8-digit code sent to ${email}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-4">
              <Input
                type="email"
                required
                autoFocus
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sending}
              />
              <Button type="submit" className="w-full" disabled={sending || !email}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send code'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={8}
                  value={code}
                  onChange={onCodeChange}
                  disabled={verifying}
                  autoFocus
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {verifying && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying…
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button variant="ghost" className="w-full" onClick={resend} disabled={sending || verifying}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend code'}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                  }}
                  disabled={verifying}
                >
                  Use a different email
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
